import React, { useState, useEffect } from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  ReferenceField,
  useRecordContext,
  EditButton,
  ListButton,
  TopToolbar,
  useRedirect,
  Labeled,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  LocationOn as ZoneIcon,
  Factory as PlantIcon,
  Edit as EditIcon,
  List as ListIcon,
  TableChart as TableIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { dataProvider } from '@/providers/dataProvider';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';

const ShowTitle = () => {
  const record = useRecordContext();
  return record ? `Zona: ${record.zoneName}` : 'Zona';
};

const TablesListSection = () => {
  const record = useRecordContext();
  const redirect = useRedirect();
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (record?.id) {
      const fetchTables = async () => {
        setLoading(true);
        setError(null);
        try {
          const response = await dataProvider.getList('tables', {
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'tableName', order: 'ASC' },
            filter: { zoneId: record.id },
          });
          setTables(response.data);
        } catch (err) {
          setError('La zona no contiene ninguna tabla asociada.');
          console.error('Error fetching tables:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchTables();
    }
  }, [record?.id]);

  if (!record) return null;

  return (
    <Card
      sx={{
        boxShadow: 3,
        borderRadius: 2,
        backgroundColor: 'background.default',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: 6,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <TableIcon color='primary' />
          <Typography variant='h6' fontWeight='bold'>
            Tablas en esta Zona
          </Typography>
          <Chip
            label={tables.length}
            size='small'
            color='primary'
            sx={{ ml: 'auto' }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Alert severity='error' sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : tables.length > 0 ? (
          <Paper
            sx={{
              maxHeight: 300,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <List dense>
              {tables.map((table: any) => (
                <ListItem
                  key={table.id}
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  secondaryAction={
                    <IconButton
                      edge='end'
                      onClick={() => redirect(`/tables/${table.id}/show`)}
                      size='small'
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                          color: 'primary.contrastText',
                        },
                      }}
                    >
                      <ViewIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <TableIcon color='primary' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant='body2' fontWeight='medium'>
                        {table.tableName}
                      </Typography>
                    }
                    secondary={
                      <Typography variant='caption' color='text.secondary'>
                        ID: {table.id}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ py: 2, textAlign: 'center' }}
          >
            No hay tablas asociadas a esta zona
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const ShowActions = () => (
  <TopToolbar>
    <EditButton icon={<EditIcon />} label='Editar' />
    <DeleteWithPasswordButton resource='zones' label='Eliminar' />
    <ListButton icon={<ListIcon />} label='Volver a la Lista' />
  </TopToolbar>
);

export const ZonesShow = () => {
  return (
    <Show title={<ShowTitle />} actions={<ShowActions />}>
      <Box sx={{ padding: 3, margin: '0 auto' }}>
        <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
          <CardContent>
            <Box display='flex' alignItems='center' mb={3}>
              <ZoneIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
              <Box>
                <Typography variant='h5' component='h1' fontWeight='bold'>
                  Detalles de la Zona
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Información completa de la zona
                </Typography>
              </Box>
            </Box>

            <SimpleShowLayout>
              <Grid container spacing={3}>
                {/* Información Principal */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <ZoneIcon color='primary' />
                        <Typography variant='h6' fontWeight='bold'>
                          Información de la Zona
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <Labeled label='Id de la zona'>
                          <TextField source='id' />
                        </Labeled>
                        <Labeled label='Nombre de la Zona'>
                          <TextField source='zoneName' />
                        </Labeled>
                      </div>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Planta Asociada */}
                <Grid item xs={12} md={6}>
                  <Card
                    sx={{
                      height: 'fit-content',
                      boxShadow: 2,
                      borderRadius: 2,
                      backgroundColor: 'background.default',
                    }}
                  >
                    <CardContent>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <PlantIcon color='primary' />

                        <Typography variant='h6' fontWeight='bold'>
                          Planta Asociada
                        </Typography>
                      </Box>
                      <Divider sx={{ mb: 2 }} />

                      <ReferenceField
                        source='plantId'
                        reference='plants'
                        label='Planta'
                        link='show'
                      >
                        <TextField source='plantName' />
                      </ReferenceField>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Tablas Asociadas */}
              <Grid container spacing={3} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <TablesListSection />
                </Grid>
              </Grid>
            </SimpleShowLayout>
          </CardContent>
        </Card>
      </Box>
    </Show>
  );
};

export default ZonesShow;
